import { In } from 'typeorm'
import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Attachment } from '@things-factory/attachment-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { KpiMetric } from './kpi-metric'
import { KpiMetricList } from './kpi-metric-type'
import { DataSet } from '@things-factory/dataset'
import { dedupByClosestDomain, getDomainIdsWithAncestors } from '../utils/domain-inheritance'

@Resolver(KpiMetric)
export class KpiMetricQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiMetric!, { nullable: true, description: 'Fetch a single KPI metric by its unique identifier.' })
  async kpiMetric(
    @Arg('id', { description: 'Unique identifier of the KPI metric to fetch.' }) id: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetric> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    return await getRepository(KpiMetric).findOne({
      where: { domain: { id: In(domainIds) }, id },
      relations: ['dataSet']
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiMetricList, { description: 'To fetch multiple KpiMetrics' })
  async kpiMetrics(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricList> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    // closest-wins dedup 을 위해 pagination 은 dedup 이후 직접 적용한다.
    const { pagination, ...rest } = params || ({} as ListParam)

    // alias 를 명시해야 정렬 식별자가 `<alias>.rank` 로 한정(qualified)되어
    // rank(예약어)도 정상 정렬된다. alias 미지정 시 bare `rank` 로 나가 집계함수로 오인됨.
    const queryBuilder = getQueryBuilderFromListParams({
      domain: undefined,
      params: rest as ListParam,
      repository: getRepository(KpiMetric),
      alias: 'kpiMetric',
      searchables: ['name', 'description']
    })

    queryBuilder.andWhere(`${queryBuilder.alias}.domain IN (:...ancestorDomainIds)`, {
      ancestorDomainIds: domainIds
    })

    const allItems = await queryBuilder.getMany()
    const deduped = dedupByClosestDomain(allItems, domainIds, m => m.name)

    const { page = 1, limit } = pagination || {}
    const total = deduped.length
    const items =
      limit && limit > 0 ? deduped.slice(Math.max(0, (page - 1) * limit), Math.max(0, (page - 1) * limit) + limit) : deduped

    return { items, total }
  }

  @FieldResolver(type => String)
  async thumbnail(@Root() kpiMetric: KpiMetric): Promise<string | undefined> {
    const attachment: Attachment = await getRepository(Attachment).findOne({
      where: {
        domain: { id: kpiMetric.domainId },
        refType: KpiMetric.name,
        refBy: kpiMetric.id
      }
    })

    return attachment?.fullpath
  }

  @FieldResolver(type => String)
  async dataSet(@Root() kpiMetric: KpiMetric): Promise<DataSet> {
    return kpiMetric.dataSetId && (await getRepository(DataSet).findOneBy({ id: kpiMetric.dataSetId }))
  }

  @FieldResolver(type => Domain)
  async domain(@Root() kpiMetric: KpiMetric): Promise<Domain> {
    return kpiMetric.domainId && (await getRepository(Domain).findOneBy({ id: kpiMetric.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() kpiMetric: KpiMetric): Promise<User> {
    return kpiMetric.updaterId && (await getRepository(User).findOneBy({ id: kpiMetric.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() kpiMetric: KpiMetric): Promise<User> {
    return kpiMetric.creatorId && (await getRepository(User).findOneBy({ id: kpiMetric.creatorId }))
  }
}
